import React from 'react';

import classNames from 'classnames';

import Button from '@/components/shared/Button/Button';
import { ExploreBlock } from '@addmile/backend-client';

import { BLOCK_PERSONALIZATIONS, PERSONALIZATION_COLORS } from '../../const';
import ContentStatusComponent from '../ContentStatus/ContentStatus';

interface BlockCardProps {
  block: ExploreBlock;
  onEditBlock: (slug: string) => void;
  onDeleteBlock: (slug: string) => void;
  onCopyBlock: (slug: string) => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  shouldShowMoveButtons?: boolean;
  isReordering?: boolean;
}

export const BlockCard: React.FC<BlockCardProps> = ({
  block,
  onEditBlock,
  onDeleteBlock,
  onCopyBlock,
  canMoveUp,
  canMoveDown,
  onMoveUp,
  onMoveDown,
  shouldShowMoveButtons = false,
  isReordering = false,
}) => {
  const personalizationImage = BLOCK_PERSONALIZATIONS[block.type];
  const personalizations = block.personalizations || [];

  return (
    <div className="flex items-center justify-between rounded-2xl bg-white-100 p-5 transition-all duration-200">
      <div className="flex gap-3 justify-start items-center h-full">
        {shouldShowMoveButtons && (
          <div className="flex flex-col items-center gap-1 mr-2">
            <button
              className="w-8 h-8 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              aria-label="Move up"
              title={isReordering ? 'Repositioning in progress...' : 'Move up'}
            >
              <img src="/icons/arrow-up.svg" alt="move up" />
            </button>
            <button
              className="w-8 h-8 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              aria-label="Move down"
              title={isReordering ? 'Repositioning in progress...' : 'Move down'}
            >
              <img src="/icons/arrow-up.svg" alt="move down" className="rotate-180" />
            </button>
          </div>
        )}
        {personalizationImage && (
          <img
            src={personalizationImage}
            alt={block.title}
            className="rounded-full m-auto py-2 min-w-12 min-h-12"
          />
        )}
        <div className="flex flex-col gap-6 justify-between h-full">
          <div className="flex gap-2 items-center">
            <p className="text-2xl font-bold text-neutral-90">{block.title}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {personalizations.map((personalization) => (
              <span
                key={personalization}
                className={classNames(
                  'text-medium px-2 py-1 rounded-lg w-fit',
                  PERSONALIZATION_COLORS[personalization] || 'bg-neutral-10',
                )}
              >
                {personalization}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <ContentStatusComponent status={block.status} />
        <Button
          isIcon
          className="!border-neutral-20 ml-2"
          variant="transparent"
          prefixIcon={<img src="/icons/pencil.svg" alt="edit" />}
          onClick={() => onEditBlock(block.slug)}
        />

        <Button
          isIcon
          className="!border-neutral-20 ml-2"
          variant="transparent"
          prefixIcon={<img src="/icons/copy.svg" alt="copy" />}
          onClick={() => onCopyBlock(block.slug)}
        />

        <Button
          isIcon
          className="border-none"
          variant="transparent"
          prefixIcon={<img src="/icons/trash.svg" alt="delete" />}
          onClick={() => onDeleteBlock(block.slug)}
        />
      </div>
    </div>
  );
};

export default BlockCard;
